import { useReplayStore } from "./replay.js";
import { create } from "zustand";
import type { AttackResponse, TargetRow } from "@empire/shared";
import { ApiProblem, Unreachable } from "../api/client.js";
import { fetchTargets, launchAttack, newIdempotencyKey } from "../api/attack.js";
import { useIntelStore } from "./intel.js";
import { toPlan, usePlanStore } from "./plan.js";
import { useSessionStore } from "./session.js";
import { useVillageStore } from "./village.js";

/**
 * Picking a target and sending an army (S5-5 client side, S3-4).
 *
 * The idempotency key is the interesting part. It is minted when the confirm
 * sheet OPENS and reused for every send from that sheet, so a tap that times
 * out and is tapped again reaches the server with the same key and gets back
 * the battle it already resolved. Minting one per tap would make the whole
 * mechanism decorative.
 */

export type LaunchStatus = "idle" | "sending" | "sent";

interface AttackState {
  targets: TargetRow[];
  /** The player's own village square, for centring the map. */
  home: { x: number; y: number } | null;
  /** When the target list last arrived; null until the first answer. */
  targetsLoadedAt: number | null;
  loadingTargets: boolean;
  targetsError: string | null;

  /** The village the confirm sheet is about, or null when it is closed. */
  confirming: TargetRow | null;
  idempotencyKey: string | null;

  status: LaunchStatus;
  error: string | null;
  /** The battle that just happened, for the result screen. */
  outcome: AttackResponse | null;

  loadTargets: () => Promise<void>;
  confirm: (target: TargetRow) => void;
  cancel: () => void;
  send: () => Promise<void>;
  dismissOutcome: () => void;
  /**
   * Back to the map from a finished battle. The result used to stay on the Map
   * tab until "Plan another attack", and even then the planner kept the tab —
   * friends test #2 found no way back to the map at all.
   */
  leave: () => void;
}

export const useAttackStore = create<AttackState>((set, get) => ({
  targets: [],
  home: null,
  targetsLoadedAt: null,
  loadingTargets: false,
  targetsError: null,

  confirming: null,
  idempotencyKey: null,

  status: "idle",
  error: null,
  outcome: null,

  loadTargets: async () => {
    const token = await useSessionStore.getState().token();
    if (!token) return;
    set({ loadingTargets: true, targetsError: null });
    try {
      const { targets, home } = await fetchTargets(token);
      set({ targets, home, loadingTargets: false, targetsLoadedAt: Date.now() });
    } catch (error) {
      set({ loadingTargets: false, targetsError: describe(error) });
    }
  },

  confirm: (target) => set({ confirming: target, idempotencyKey: newIdempotencyKey(), error: null }),

  cancel: () => set({ confirming: null, idempotencyKey: null, error: null }),

  send: async () => {
    const { confirming, idempotencyKey, status } = get();
    if (!confirming || !idempotencyKey || status === "sending") return;

    // Claim "sending" BEFORE the first await, not after.
    //
    // Getting a token can go to the network, and a second tap during that gap
    // would find the status still "idle" and sail straight past the guard —
    // two requests, same key. The server would deduplicate them, so nothing
    // would break, but the guard here would be doing nothing at all and the
    // moment the key was ever wrong it would cost somebody two armies.
    set({ status: "sending", error: null });

    const { villageId } = useSessionStore.getState();
    const token = await useSessionStore.getState().token();
    if (!villageId || !token) {
      set({ status: "idle", error: "Not connected to the server." });
      return;
    }

    try {
      const outcome = await launchAttack(token, {
        fromVillageId: villageId,
        toVillageId: confirming.villageId,
        plan: toPlan(usePlanStore.getState().waves),
        idempotencyKey,
      });
      // Keep the target. The result screen tells a held attacker to attack
      // again to see more of the maze — and then forgetting who they were
      // attacking makes them go and find the name again. The KEY is cleared,
      // though: the next attack must be a new one, not a retry of this.
      set({ status: "sent", outcome, idempotencyKey: null });
      // The army just got smaller (D-109). The answer carries the village it was
      // paid for out of, so the planner's chips and the village screen both drop
      // to the truth immediately rather than on the next fifteen-second poll —
      // which is the window in which somebody would otherwise queue an attack
      // out of units that are already dead.
      useVillageStore.getState().receive(outcome.village);
      // The target's counters changed, and "attacked 3 times" going stale is
      // how a tester ends up attacking the same maze twice by accident.
      void get().loadTargets();
      // And so did what you know about that village — which is the whole point
      // of the attack that just happened. The panel fetches once per target, so
      // without this the intel shown after a battle is the intel from before
      // it: permanently one attack behind, and blank after the first one.
      void useIntelStore.getState().load(confirming.villageId);
      // The battle is the payoff: play it straight away, and let the report
      // wait underneath for when the replay is closed. Last, so the refreshes
      // above are already on their way when the replay's own fetch goes out.
      void useReplayStore.getState().open(outcome.battleId, "attacker");
    } catch (error) {
      // The key is deliberately KEPT: whatever went wrong, trying again must
      // reuse it, or a request that actually landed would resolve twice.
      set({ status: "idle", error: describe(error) });
    }
  },

  leave: () =>
    set({ outcome: null, confirming: null, status: "idle", idempotencyKey: null, error: null }),

  dismissOutcome: () =>
    set((state) => ({
      outcome: null,
      status: "idle",
      // A fresh key for the next attack, minted here rather than on send for
      // the same reason as the first one: a retry of THAT attack must reuse it.
      idempotencyKey: state.confirming ? newIdempotencyKey() : null,
    })),
}));

/**
 * The server's own words where it has them.
 *
 * Every refusal from /attacks is something a player can act on — the army costs
 * too much, that village has no defence yet — so replacing them with "Couldn't
 * attack" would throw away the only useful part.
 */
function describe(error: unknown): string {
  if (error instanceof Unreachable) {
    return "Can't reach the server. Nothing was sent — try again in a moment.";
  }
  if (error instanceof ApiProblem) return error.message;
  return "Something went wrong. Nothing was sent.";
}
